import type {
  AddressOrNative,
  AgentSwapMode,
  ExplicitAddress,
} from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Token } from '@ui/config/token';
import { useSDK } from '@ui/utils';
import type { Fraction } from 'bi-fraction';

import { useAgents } from '../api/useAgents';
import { usePriceHistory } from '../api/usePriceHistory';
import { useSearchAgents } from '../api/useSearchAgents';
import { getTokenBalanceQueryKey } from '../contracts/useTokenBalance';
import { getTokenPriceQueryKey } from '../tokenPrice';

export const useH2SwapMutation = (onSuccess?: (txHash?: string) => void) => {
  const sdk = useSDK();
  const provider = currentWallet.useProvider();
  const queryClient = useQueryClient();

  return useMutation(
    async (params: {
      amountIn: Fraction;
      inputTokenAddress: AddressOrNative;
      inputTokenDecimals: number;
      outputTokenAddress: ExplicitAddress;
      outputTokenDecimals: number;
      slippage: Fraction;
      mode: AgentSwapMode;
      bondingAddress: ExplicitAddress;
    }) => {
      const signer = await provider?.getSigner();
      if (!signer) throw new Error('no signer');
      const swapModule = sdk.getSwapModule(signer);
      return swapModule.swap(params);
    },
    {
      onSuccess: (
        result,
        { inputTokenAddress, outputTokenAddress, bondingAddress },
      ) => {
        queryClient.invalidateQueries({
          queryKey: getTokenBalanceQueryKey(inputTokenAddress),
        });
        queryClient.invalidateQueries({
          queryKey: getTokenBalanceQueryKey(outputTokenAddress),
        });
        queryClient.invalidateQueries({
          queryKey: getTokenPriceQueryKey({
            type: Token.Bonding,
            bondingAddress,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: ['useBondingTokenOrAgentTokenPrice'],
        });
        queryClient.invalidateQueries([useAgents.queryKey]);
        queryClient.invalidateQueries({
          queryKey: [useSearchAgents.queryKey],
        });
        queryClient.invalidateQueries(['useAgent']);
        queryClient.invalidateQueries([usePriceHistory.queryKey]);
        onSuccess?.(result?.hash);
      },
    },
  );
};
